import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Lightbulb,
  Search,
  Wrench,
  Rocket,
  Gauge,
  TrendingUp,
} from 'lucide-react';

/** Six stages, Raise + Scale merged for cleaner spacing */
const STEPS = [
  {
    title: 'Ideate',
    capability: 'Strategy',
    desc: 'Shape the opportunity, venture intent and strategic direction.',
    Icon: Lightbulb,
  },
  {
    title: 'Validate',
    capability: 'Brand',
    desc: 'Test market, customer and positioning before committing to build.',
    Icon: Search,
  },
  {
    title: 'Build',
    capability: 'Technology',
    desc: 'Engineer product, systems and operating foundations.',
    Icon: Wrench,
  },
  {
    title: 'Go to Market',
    capability: 'Talent',
    desc: 'Assemble teams and launch into the market with clarity.',
    Icon: Rocket,
  },
  {
    title: 'Accelerate',
    capability: 'Market Access',
    desc: 'Drive traction, distribution and growth momentum.',
    Icon: Gauge,
  },
  {
    title: 'Raise & Scale',
    capability: 'Capital',
    desc: 'Raise when ready, then deploy capital, talent and partnerships with discipline.',
    Icon: TrendingUp,
  },
];

/** Even 6-node wave with room above/below for cards */
const WAVE_PATH =
  'M 80 200 C 160 80, 240 80, 320 200 S 480 320, 560 200 S 720 80, 800 200 S 880 300, 920 200';

export default function LifecycleStrip() {
  const trackRef = useRef(null);
  const pathRef = useRef(null);
  const [active, setActive] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [pathLen, setPathLen] = useState(0);
  const [nodePts, setNodePts] = useState([]);
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const sync = () => setIsDesktop(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    const path = pathRef.current;
    if (!path || !isDesktop) return;
    const len = path.getTotalLength();
    setPathLen(len);
    const pts = STEPS.map((_, i) => {
      const t = STEPS.length === 1 ? 0 : i / (STEPS.length - 1);
      const p = path.getPointAtLength(t * len);
      return { x: p.x, y: p.y };
    });
    setNodePts(pts);
  }, [isDesktop]);

  useEffect(() => {
    if (!isDesktop) return undefined;
    const track = trackRef.current;
    if (!track) return undefined;

    const onScroll = () => {
      const rect = track.getBoundingClientRect();
      const total = track.offsetHeight - window.innerHeight;
      if (total <= 0) { setActive(0); setScrollProgress(0); return; }
      const scrolled = Math.min(Math.max(-rect.top, 0), total);
      const progress = scrolled / total;
      setScrollProgress(progress);
      setActive(Math.min(STEPS.length - 1, Math.floor(progress * STEPS.length)));
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [isDesktop]);

  // Use continuous scrollProgress for smooth path drawing (not discrete steps)
  const dashOffset = useMemo(() => {
    if (!pathLen) return pathLen || 9999;
    return pathLen * (1 - scrollProgress);
  }, [scrollProgress, pathLen]);

  return (
    <section
      id="lifecycle"
      ref={trackRef}
      className="relative bg-[#08080a] border-t border-white/5"
      style={isDesktop ? { height: `${STEPS.length * 100}vh` } : undefined}
    >
      <div
        className={
          isDesktop
            ? 'sticky top-0 h-screen flex flex-col justify-center overflow-hidden'
            : 'py-16 sm:py-20'
        }
      >
        <div className="w-full max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-8 lg:pt-10 pb-4 lg:pb-6 shrink-0">
          <div className="text-center max-w-3xl mx-auto">
            <div className="text-[#ff8ec5] text-xs tracking-[0.3em] uppercase mb-3">
              Across the venture lifecycle
            </div>
            <h2 className="font-semibold tracking-tight text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl leading-tight">
              We build across the{' '}
              <span className="text-[#d42b92]">venture lifecycle</span>
            </h2>
            <p className="mt-4 text-white/50 text-sm sm:text-base leading-relaxed hidden sm:block">
              Ideate → Validate → Build → Go to Market → Accelerate → Raise &amp; Scale
            </p>
            <p className="mt-2 text-xs text-white/40">
              Step {String(active + 1).padStart(2, '0')} / {String(STEPS.length).padStart(2, '0')} -{' '}
              <span className="text-[#ff8ec5]">{STEPS[active].title}</span>
            </p>
          </div>
        </div>

        {/* Desktop wavy timeline */}
        <div className="hidden lg:block relative w-full max-w-7xl mx-auto px-8 xl:px-12 flex-1 min-h-0">
          <div className="relative h-full min-h-[540px]">
            <svg
              viewBox="0 0 1000 400"
              className="absolute inset-0 w-full h-full"
              preserveAspectRatio="xMidYMid meet"
              aria-hidden
            >
              <path
                d={WAVE_PATH}
                fill="none"
                stroke="rgba(255,255,255,0.12)"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                ref={pathRef}
                d={WAVE_PATH}
                fill="none"
                stroke="#d42b92"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray={pathLen || 1}
                strokeDashoffset={dashOffset}
                className="transition-[stroke-dashoffset] duration-500 ease-out"
              />
            </svg>

            {nodePts.map((pt, i) => {
              const step = STEPS[i];
              const Icon = step.Icon;
              const isActive = i === active;
              const isPast = i < active;
              const above = i % 2 === 0;
              const leftPct = (pt.x / 1000) * 100;
              const topPct = (pt.y / 400) * 100;

              return (
                <button
                  key={step.title}
                  type="button"
                  onClick={() => setActive(i)}
                  className="absolute z-10 group text-left"
                  style={{
                    left: `${leftPct}%`,
                    top: `${topPct}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  <div
                    className={`absolute w-[168px] xl:w-[188px] left-1/2 -translate-x-1/2 rounded-2xl border px-4 py-3.5 transition-all duration-500 pointer-events-none ${
                      above ? 'bottom-[calc(100%+32px)]' : 'top-[calc(100%+32px)]'
                    } ${
                      isActive
                        ? 'border-[#d42b92] bg-[#140914] shadow-[0_16px_48px_-12px_rgba(212,43,146,0.55)] opacity-100 scale-105'
                        : isPast
                          ? 'border-white/20 bg-white/[0.05] opacity-95'
                          : 'border-white/12 bg-[#0c0c10] opacity-80 group-hover:opacity-100'
                    }`}
                  >
                    <div
                      className={`text-[10px] uppercase tracking-[0.16em] mb-1 ${
                        isActive ? 'text-[#ff8ec5]' : 'text-white/45'
                      }`}
                    >
                      {step.capability}
                    </div>
                    <div className="text-sm font-semibold text-white leading-snug">{step.title}</div>
                    <p className="mt-1.5 text-[11px] text-white/55 leading-relaxed line-clamp-3">
                      {step.desc}
                    </p>
                  </div>

                  <span
                    className={`absolute left-1/2 -translate-x-1/2 w-px h-6 ${
                      above ? 'bottom-full mb-1.5' : 'top-full mt-1.5'
                    } ${isActive || isPast ? 'bg-[#d42b92]/55' : 'bg-white/15'}`}
                    aria-hidden
                  />

                  <div
                    className={`w-12 h-12 xl:w-[52px] xl:h-[52px] rounded-full grid place-items-center border-2 transition-all duration-500 ${
                      isActive
                        ? 'bg-[#d42b92] border-[#d42b92] text-white shadow-[0_0_28px_rgba(212,43,146,0.55)] scale-110'
                        : isPast
                          ? 'bg-[#d42b92]/40 border-[#d42b92] text-white'
                          : 'bg-[#08080a] border-white/30 text-white/55 group-hover:border-white/50'
                    }`}
                  >
                    <Icon className="w-[18px] h-[18px]" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Mobile / tablet */}
        <div className="lg:hidden px-5 sm:px-6 max-w-xl mx-auto w-full mt-8">
          <div className="relative pl-10">
            <div className="absolute left-[15px] top-3 bottom-3 w-px bg-white/10" />
            <div
              className="absolute left-[15px] top-3 w-px bg-[#d42b92] transition-all duration-500"
              style={{ height: `calc(${(active / Math.max(STEPS.length - 1, 1)) * 100}% + 12px)` }}
            />
            <div className="space-y-5">
              {STEPS.map((step, i) => {
                const Icon = step.Icon;
                const isActive = i === active;
                const isPast = i < active;
                return (
                  <button
                    key={step.title}
                    type="button"
                    onClick={() => setActive(i)}
                    className="relative block w-full text-left"
                  >
                    <div
                      className={`absolute -left-10 top-4 w-8 h-8 rounded-full grid place-items-center border-2 transition-colors ${
                        isActive || isPast
                          ? 'bg-[#d42b92] border-[#d42b92] text-white'
                          : 'bg-[#08080a] border-white/25 text-white/40'
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <div
                      className={`rounded-2xl border p-4 sm:p-5 transition-all ${
                        isActive
                          ? 'border-[#d42b92] bg-[#140914] shadow-[0_12px_36px_-12px_rgba(212,43,146,0.45)]'
                          : 'border-white/10 bg-white/[0.03]'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#ff8ec5]">
                          {step.capability}
                        </div>
                        <div className="text-[10px] tracking-[0.16em] uppercase text-white/30">
                          {String(i + 1).padStart(2, '0')}
                        </div>
                      </div>
                      <div className="mt-1.5 text-base sm:text-lg font-semibold text-white">
                        {step.title}
                      </div>
                      <p className="mt-2 text-sm text-white/55 leading-relaxed">{step.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="hidden lg:flex flex-col items-center pb-8 pt-2 text-[11px] uppercase tracking-[0.25em] text-white/35 shrink-0">
          <span>Keep scrolling</span>
          <span className="mt-2 w-px h-8 bg-gradient-to-b from-[#d42b92] to-transparent" />
        </div>
      </div>
    </section>
  );
}
